import { useEffect, useState } from "react";
import { range } from "lodash";
import DatePicker from "react-datepicker";
import PropTypes from "prop-types";

import { months, strCode } from "../../consts";
import { t } from "../../services";
import { dateUtil } from "../../utils";

import {
  CalendarDropdownInput,
  FlexContainers,
  IconButton,
  SVG,
  WrapperContainer
} from "../../components";
import { InputTitleWrapper } from "../Input/styled";
import { DateInputContainer, DateInputWrapper } from "./styled";

const monthNames = months.map((month) => month.month);

const DateInput = ({
  id,
  title,
  value,
  setValue,
  maxDate,
  minDate,
  startDate,
  endDate,
  disabled,
  hasError,
  noTime,
  autoComplete
}) => {
  const defaultMaxDate = new Date();
  defaultMaxDate.setFullYear(defaultMaxDate.getFullYear() + 5);

  const [years, setYears] = useState(
    range(new Date().getFullYear(), new Date().getFullYear() + 5, 1)
  );

  useEffect(() => {
    let minYear = new Date().getFullYear();
    let maxYear = new Date().getFullYear() + 4;

    if (minDate) {
      minYear = minDate.getFullYear();
    }

    if (maxDate) {
      maxYear = maxDate.getFullYear();
    }

    setYears(range(minYear, maxYear + 1, 1));
  }, [minDate, maxDate]);

  const handleChange = (date) => {
    let res = date;

    if (id === "end" && !noTime) {
      if (dateUtil.isToday(date)) {
        const today = new Date();
        res.setHours(today.getHours(), today.getMinutes());
        res.setSeconds(today.getSeconds());
      } else {
        res.setHours(23, 59);
        res.setSeconds(59);
      }
    }

    setValue(res);
  };

  return (
    <DateInputContainer
      data-testid="input-container"
      className={disabled ? "disabled" : hasError ? "error" : ""}>
      <InputTitleWrapper>
        <label htmlFor={id}>{title}</label>
      </InputTitleWrapper>
      <DateInputWrapper value={value}>
        <DatePicker
          renderCustomHeader={({
            date,
            changeYear,
            changeMonth,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled
          }) => (
            <WrapperContainer
              Container={FlexContainers.FlexRowBetween}
              width="100%"
              ph={10}
              height={48}>
              <IconButton onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                <SVG.NavigateNextIcon flipped />
              </IconButton>

              <CalendarDropdownInput
                id="month-dropdown"
                value={monthNames[date.getMonth()]}
                onChange={(value) => changeMonth(monthNames.indexOf(value))}
                options={monthNames}
              />

              <IconButton onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                <SVG.NavigateNextIcon />
              </IconButton>

              <IconButton
                onClick={() => changeYear(date.getFullYear() - 1)}
                disabled={date.getFullYear() === years[0]}>
                <SVG.NavigateNextIcon flipped />
              </IconButton>

              <CalendarDropdownInput
                id="year-dropdown"
                value={date.getFullYear()}
                onChange={(value) => changeYear(value)}
                options={years}
              />

              <IconButton
                onClick={() => changeYear(date.getFullYear() + 1)}
                disabled={date.getFullYear() === years[years.length - 1]}>
                <SVG.NavigateNextIcon />
              </IconButton>
            </WrapperContainer>
          )}
          id={id}
          selected={value}
          dateFormat="dd-MM-yy"
          formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 1)}
          onChange={(date) => handleChange(date)}
          placeholderText={t(strCode.DATE_INPUT_PLACEHOLER)}
          disabled={disabled}
          startDate={startDate}
          endDate={endDate}
          maxDate={maxDate || defaultMaxDate}
          minDate={minDate || new Date()}
          autoComplete={autoComplete ? autoComplete : "off"}
          portalId={"date-input-portal"}
        />

        <SVG.CalendarIcon />
      </DateInputWrapper>
    </DateInputContainer>
  );
};

DateInput.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  value: PropTypes.instanceOf(Date),
  setValue: PropTypes.func.isRequired,
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
  noTime: PropTypes.bool,
  autoComplete: PropTypes.bool
};

export default DateInput;
